// src/apps/merchant3/hooks/useConfigurations.ts
import { computed, ComputedRef } from "vue";
import { getConfigurations } from "@/apps/merchant3/services/configurations";
import { store } from "@/shared-store";
import { IVuexState } from "@/apps/merchant3/shared-models/IVuex";
import { Store } from "vuex";
import { IConfigurationResponse } from "@/apps/merchant3/models/IConfigurationResponse";

interface ConfigurationsHook {
  components: ComputedRef<Record<string, boolean> | null>;
  globalComponents: ComputedRef<Record<string, boolean> | null>;
  flows: ComputedRef<Record<string, boolean> | null>;
  settings: ComputedRef<Record<string, unknown> | null>;
  currentStep: ComputedRef<string | null>;
  nextStep: ComputedRef<string | null>;
  configResponse: ComputedRef<IConfigurationResponse | null>;
  errorCode: ComputedRef<number | null>;
  isConfigLoaded: ComputedRef<boolean>;
  hasValidConfigs: ComputedRef<boolean>;
  loadConfigs: (cartTag: string, stepName: string) => Promise<void>;
}

export default function useConfigurations(): ConfigurationsHook {
  const vuexStore = store as unknown as Store<IVuexState>;

  const loadConfigs = async (cartTag: string, stepName: string) => {
    const resp = await getConfigurations(cartTag, stepName);
    vuexStore.dispatch("configurations/setConfigurations", resp);
  };

  const configResponse = computed(
    () => vuexStore.state.configurations as IConfigurationResponse | null
  );

  const components = computed(() => configResponse.value?.components ?? null);

  const globalComponents = computed(
    () => configResponse.value?.globalComponents ?? null
  );

  const flows = computed(() => configResponse.value?.flows ?? null);

  const settings = computed(() => configResponse.value?.settings ?? null);

  const currentStep = computed(() => configResponse.value?.currentStep ?? null);

  const nextStep = computed(() => configResponse.value?.nextStep ?? null);

  const isConfigLoaded = computed(
    () => !!configResponse.value && Object.keys(configResponse.value).length > 0
  );

  const errorCode = computed(() => configResponse.value?.errorCode ?? null);

  const hasValidConfigs = computed(
    () =>
      !!components.value &&
      Object.keys(components.value).length > 0 &&
      errorCode.value === null
  );

  return {
    components,
    globalComponents,
    flows,
    settings,
    currentStep,
    nextStep,
    configResponse,
    errorCode,
    isConfigLoaded,
    hasValidConfigs,
    loadConfigs,
  };
}
